import flagsmith from '@flagsmith/flagsmith';
import { FlagsmithProvider } from '@flagsmith/flagsmith/react';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { VITE_APP_ENV, VITE_FLAGSMITH_ENV_KEY } from '@/constants/constants';
import { CurrencyProvider } from '@/context/CurrencyContext/CurrencyContext';
import QueryClientProvider from '@/context/QueryClient/QueryClientProvider';
import Entry from '@/screens/Entry/Entry';
import NotFound from '@/screens/NotFound/NotFound';
import { PaymentConfirmation } from '@/screens/PaymentStatus/PaymentConfirmation';
import PaymentFailure from '@/screens/PaymentStatus/PaymentFailure';
import './App.css';
import LegalDocumentPage from '@/screens/LegalDocument/LegalDocumentPage';
import { useBrowserLanguage } from './hooks/useBrowserLanguage';
import { useBrowserUserId } from './hooks/useBrowserUserId';
import { useLanguage } from './hooks/useLanguage';
import { CheckoutWithFeatureFlag } from './screens/CheckoutWithFeatureFlag/CheckoutWithFeatureFlag';
import MenuItemsListing from './screens/Menu/MenuItemsListing';

export const SUCCESS_URL = '/checkout/success';
export const FAIL_URL = '/checkout/failure';

export const BASE_URL = '/';
export const MENU_URL = '/menu';
export const ENTRY_URL = '/entry';
const TERMS_URL = '/terms';
const PRIVACY_URL = '/privacy';

const LANG_MENU_URL = '/:lang/menu';

function MenuRedirect({ defaultLanguage }: { defaultLanguage: string }) {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{
        pathname: `/${defaultLanguage}/menu`,
        search: location.search,
        hash: location.hash,
      }}
    />
  );
}

// Component to set dir attribute on body and html elements
function DirSetter() {
  const { isRTL, language } = useLanguage();

  useEffect(() => {
    // Set dir attribute on body and html elements
    document.body.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', language === 'ar' ? 'ar' : 'en');

    return () => {
      document.body.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'en');
    };
  }, [isRTL, language]);

  return null;
}

function App() {
  const browserUserId = useBrowserUserId();
  const { getStoredLanguage } = useBrowserLanguage();

  useEffect(() => {
    flagsmith
      .init({
        environmentID: VITE_FLAGSMITH_ENV_KEY,
        identity: browserUserId,
      })
      .catch(() => {
        // Network errors are expected (e.g. offline), silently ignored.
      });
    Sentry.setUser({ id: browserUserId });
  }, [browserUserId]);

  const defaultLanguage = getStoredLanguage();

  return (
    <QueryClientProvider>
      <FlagsmithProvider flagsmith={flagsmith}>
        <CurrencyProvider>
          <BrowserRouter>
            <DirSetter />
            <Routes>
              <Route path={BASE_URL} element={<Entry />} />
              <Route path={ENTRY_URL} element={<CheckoutWithFeatureFlag />} />
              <Route path="*" element={<NotFound />} />

              {/* Language-aware menu routes */}
              <Route path={LANG_MENU_URL} element={<MenuItemsListing />} />

              <Route path={MENU_URL} element={<MenuRedirect defaultLanguage={defaultLanguage} />} />

              <Route path={`${SUCCESS_URL}/*`} element={<PaymentConfirmation />} />
              <Route path={`${FAIL_URL}/*`} element={<PaymentFailure />} />

              <Route path={TERMS_URL} element={<Navigate replace to="/terms/ae/en" />} />
              <Route path={PRIVACY_URL} element={<Navigate replace to="/privacy/ae/en" />} />
              <Route
                path={`${TERMS_URL}/:country/:lang`}
                element={<LegalDocumentPage docType="terms" />}
              />
              <Route
                path={`${PRIVACY_URL}/:country/:lang`}
                element={<LegalDocumentPage docType="privacy" />}
              />
            </Routes>
          </BrowserRouter>
        </CurrencyProvider>
      </FlagsmithProvider>
      {VITE_APP_ENV !== 'prod' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

const AppWithProfiler = Sentry.withProfiler(App);
// ts-prune-ignore-next
export default AppWithProfiler;
